import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200/50 bg-white/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-bold tracking-tighter uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
              <span className="text-slate-900">NETEXEC</span>
              <span className="text-orange-500"> GLOBAL</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-orange-500 ${
                isActive('/') ? 'text-orange-500' : 'text-slate-700'
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-orange-500 ${
                isActive('/about') ? 'text-orange-500' : 'text-slate-700'
              }`}
            >
              About
            </Link>
            <div className="relative group">
              <button
                className="text-sm font-medium text-slate-700 transition-colors hover:text-orange-500 flex items-center gap-1"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                Services
                <ChevronDown className="w-4 h-4" />
              </button>
              {servicesOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-64 bg-white border border-slate-200 shadow-xl rounded-sm"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <Link
                    to="/services"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    All Services
                  </Link>
                  <Link
                    to="/services/freight-forwarding"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Freight Forwarding
                  </Link>
                  <Link
                    to="/services/customs-clearance"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Customs Clearance
                  </Link>
                  <Link
                    to="/services/air-freight"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Air Freight
                  </Link>
                  <Link
                    to="/services/ocean-freight"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Ocean Freight
                  </Link>
                  <Link
                    to="/services/import-solutions"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Import Solutions
                  </Link>
                  <Link
                    to="/services/export-solutions"
                    className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:text-orange-500 transition-colors"
                  >
                    Export Solutions
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/trade-routes"
              className={`text-sm font-medium transition-colors hover:text-orange-500 ${
                isActive('/trade-routes') ? 'text-orange-500' : 'text-slate-700'
              }`}
            >
              Trade Routes
            </Link>
            <Link
              to="/industries"
              className={`text-sm font-medium transition-colors hover:text-orange-500 ${
                isActive('/industries') ? 'text-orange-500' : 'text-slate-700'
              }`}
            >
              Industries
            </Link>
            <Link
              to="/faq"
              className={`text-sm font-medium transition-colors hover:text-orange-500 ${
                isActive('/faq') ? 'text-orange-500' : 'text-slate-700'
              }`}
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              data-testid="nav-get-quote-btn"
              className="bg-slate-900 text-white px-6 py-2.5 rounded-sm text-sm font-medium hover:bg-orange-500 transition-colors duration-300"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-slate-700"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-4 space-y-2" data-testid="mobile-menu">
            <Link
              to="/"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              About
            </Link>
            <Link
              to="/services"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/trade-routes"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              Trade Routes
            </Link>
            <Link
              to="/industries"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              Industries
            </Link>
            <Link
              to="/faq"
              className="block py-2 text-sm text-slate-700 hover:text-orange-500"
              onClick={() => setIsOpen(false)}
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              className="block py-2 text-sm bg-slate-900 text-white px-4 rounded-sm hover:bg-orange-500 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              Get a Quote
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;